import { fetchApi } from './client'

export interface Workflow {
  id: string
  name: string
  description: string
  isLatest?: boolean
}

export interface WorkflowsResponse {
  workflows: Workflow[]
}

/**
 * Fetch available workflows.
 */
export async function getWorkflows(): Promise<Workflow[]> {
  try {
    const response = await fetchApi<WorkflowsResponse>('/workflows')
    return response.workflows || []
  } catch (error) {
    console.warn('Workflows endpoint not available:', error)
    // Return default workflows if endpoint unavailable
    return [
      { id: 'phenotype-aware-extraction', name: 'Router', description: 'Automatically detects carrier format' },
      { id: 'gemini-optimized-ingestion', name: 'Standard', description: 'Direct Gemini extraction' },
      { id: 'deterministic-extraction', name: 'Advanced', description: 'Rule-based extraction' },
    ]
  }
}

/**
 * Get current default workflow.
 */
export async function getLatestWorkflow(): Promise<Workflow | null> {
  try {
    return await fetchApi<Workflow>('/workflows/latest/info')
  } catch {
    return null
  }
}

/**
 * Set a workflow as the default.
 */
export async function setLatestWorkflow(workflowId: string): Promise<void> {
  await fetchApi(`/workflows/${workflowId}/set-latest`, { method: 'POST' })
}

/**
 * Get list of carriers (from addenda API).
 */
export async function getCarriers(): Promise<string[]> {
  try {
    const response = await fetchApi<{ carriers: string[] }>('/api/addenda/carriers')
    return response.carriers || []
  } catch (error) {
    console.warn('Carriers endpoint not available:', error)
    return []
  }
}

/**
 * Get current confidence threshold.
 */
export async function getConfidenceThreshold(): Promise<number> {
  const response = await fetchApi<{ threshold: number }>('/api/settings/confidence-threshold')
  return response.threshold
}

/**
 * Update confidence threshold.
 */
export async function updateConfidenceThreshold(threshold: number): Promise<void> {
  await fetchApi('/api/settings/confidence-threshold', {
    method: 'PUT',
    body: { threshold },
  })
}

// Feature flags
export interface FeatureFlag {
  key: string
  name: string
  description: string
  enabled: boolean
}

/**
 * Get all feature flags.
 */
export async function getFeatureFlags(): Promise<FeatureFlag[]> {
  const response = await fetchApi<{ flags: FeatureFlag[] }>('/api/settings/feature-flags')
  return response.flags
}

/**
 * Update a feature flag.
 */
export async function updateFeatureFlag(key: string, enabled: boolean): Promise<void> {
  await fetchApi(`/api/settings/feature-flags/${key}`, {
    method: 'PUT',
    body: { enabled },
  })
}
